import { infoToastClassName, appToast as toast } from "@/components/ui/baseUi/sonner"
import { Config } from "@/modules/config"
import { word } from "@/modules/core/utils/i18n"
import { useEffect, useRef } from "react"

const VERSION_CHECK_INTERVAL_MS = 30 * 60 * 1000 // 30 minutes

type VersionInfo = {
  version: string
  gitHash: string
  env: string
  buildTime: string
}

async function fetchVersionInfo(): Promise<VersionInfo | null> {
  try {
    const response = await fetch(`/version.json?t=${Date.now()}`)
    if (!response.ok) return null
    return (await response.json()) as VersionInfo
  } catch {
    return null
  }
}

function showUpdateToast(reason: "stale" | "new_version") {
  const messages = {
    stale: {
      title: word("global.versionCheck.stale.title"),
      description: word("global.versionCheck.stale.content"),
    },
    new_version: {
      title: word("global.versionCheck.new_version.title"),
      description: word("global.versionCheck.new_version.content"),
    },
  }

  toast.info(messages[reason].title, {
    description: messages[reason].description,
    duration: Infinity,
    classNames: infoToastClassName(),
  })
}

export function useAutoRefreshOnStale() {
  const hasNotified = useRef(false)

  useEffect(() => {
    if (Config.ENV === "dev") return

    const checkVersion = async () => {
      if (hasNotified.current) return

      const versionInfo = await fetchVersionInfo()
      if (versionInfo && versionInfo.gitHash !== __GIT_HASH__) {
        hasNotified.current = true
        showUpdateToast("new_version")
      }
    }

    const handleVisibilityChange = () => {
      if (document.visibilityState === "visible") {
        checkVersion()
      }
    }

    const handleFocus = () => {
      checkVersion()
    }

    // Periodic version check
    const versionCheckInterval = setInterval(() => void checkVersion(), VERSION_CHECK_INTERVAL_MS)

    document.addEventListener("visibilitychange", handleVisibilityChange)
    window.addEventListener("focus", handleFocus)

    return () => {
      clearInterval(versionCheckInterval)
      document.removeEventListener("visibilitychange", handleVisibilityChange)
      window.removeEventListener("focus", handleFocus)
    }
  }, [])
}
